import type { CalendarEvent, CalendarTask, CalendarContact } from '@/types/calendar.types';
import { DEMO_CONTRACT_IDS } from '../demoContractData';

function getRelativeDate(daysOffset: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  return date.toISOString().split('T')[0];
}

export const calendarContacts: CalendarContact[] = [
  {
    id: 'dj-c1',
    name: 'Kecia Tatum',
    role: 'VP Legal & Business Affairs',
    company: 'Def Jam Recordings',
    email: 'kecia.tatum@defjam.com',
    phone: '+1 (212) 333-4001',
    avatar: undefined,
    tags: ['Legal', 'Contracts', 'Business Affairs'],
    lastContact: '2 hours ago',
    isStarred: true,
  },
  {
    id: 'dj-c2',
    name: 'Nicole Billings',
    role: 'SVP A&R',
    company: 'Def Jam Recordings',
    email: 'nicole.billings@defjam.com',
    phone: '+1 (212) 333-4002',
    avatar: undefined,
    tags: ['A&R', 'Talent', 'Recording'],
    lastContact: '1 day ago',
    isStarred: true,
  },
  {
    id: 'dj-c3',
    name: 'Jeff Harleston',
    role: 'Chairman & CEO',
    company: 'Def Jam Recordings',
    email: 'jeff.harleston@defjam.com',
    phone: '+1 (212) 333-4000',
    avatar: undefined,
    tags: ['Executive', 'Strategy', 'Approvals'],
    lastContact: '3 days ago',
    isStarred: true,
  },
  {
    id: 'dj-c4',
    name: 'Rachel Weinstein',
    role: 'SVP Business & Legal Affairs',
    company: 'Universal Music Group',
    email: 'rachel.weinstein@umusic.com',
    phone: '+1 (310) 865-7200',
    avatar: undefined,
    tags: ['UMG', 'Legal', 'Parent Company'],
    lastContact: '1 week ago',
    isStarred: false,
  },
  {
    id: 'dj-c5',
    name: 'Derek Thompson',
    role: 'VP Partnerships',
    company: 'Kith',
    email: 'derek.thompson@kith.com',
    phone: '+1 (212) 204-7526',
    avatar: undefined,
    tags: ['Brand', 'Retail', 'Partnerships'],
    lastContact: '5 days ago',
    isStarred: false,
  },
  {
    id: 'dj-c6',
    name: 'Tanya Brooks',
    role: 'Brand Partnerships Manager',
    company: 'Nike',
    email: 'tanya.brooks@nike.com',
    phone: '+1 (503) 671-6453',
    avatar: undefined,
    tags: ['Brand', 'Sync', 'Marketing'],
    lastContact: '2 days ago',
    isStarred: false,
  },
];

export const calendarTasks: CalendarTask[] = [
  {
    id: 'dj-task-1',
    title: 'Review Coco Jones recording agreement terms',
    description: 'Kecia sent updated terms for the recording deal — review royalty splits and touring provisions',
    dueDate: getRelativeDate(0),
    dueTime: '17:00',
    completed: false,
    priority: 'high',
    contractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
    contactId: 'dj-c1',
    type: 'review',
  },
  {
    id: 'dj-task-2',
    title: 'Sign Phil The Keys producer deal',
    description: 'Final signature required for the producer agreement — all terms approved',
    dueDate: getRelativeDate(1),
    dueTime: '12:00',
    completed: false,
    priority: 'high',
    contractId: DEMO_CONTRACT_IDS.PRODUCER_AGREEMENT,
    contactId: 'dj-c2',
    type: 'signature',
  },
  {
    id: 'dj-task-4',
    title: 'Prepare Kith brand partnership proposal',
    description: 'Draft partnership proposal for Def Jam x Kith capsule collection',
    dueDate: getRelativeDate(2),
    completed: false,
    priority: 'medium',
    contactId: 'dj-c5',
    type: 'general',
  },
  {
    id: 'dj-task-5',
    title: 'Review Bieber Amendment final terms',
    description: 'Complete review of Album 7 amendment before signing deadline',
    dueDate: getRelativeDate(2),
    completed: true,
    priority: 'high',
    contractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
    type: 'review',
  },
  {
    id: 'dj-task-6',
    title: 'Schedule Nasty C JV call with Nasty C team',
    description: 'Set up call to discuss sublabel creative direction and territory scope',
    dueDate: getRelativeDate(3),
    completed: false,
    priority: 'low',
    contactId: 'dj-c3',
    type: 'follow-up',
  },
  {
    id: 'dj-task-7',
    title: 'Q1 catalog royalty verification',
    description: 'Verify Q1 royalty payments for Big Sean catalog have been processed correctly',
    dueDate: getRelativeDate(5),
    completed: false,
    priority: 'medium',
    contactId: 'dj-c4',
    type: 'payment',
  },
];

export const calendarEvents: CalendarEvent[] = [
  {
    id: 'dj-ev-1',
    title: 'A&R Weekly Review',
    date: getRelativeDate(0),
    startTime: '09:00',
    endTime: '10:00',
    timezone: 'EST',
    participants: ['dj-c2', 'demo_user'],
    description: 'Weekly A&R review — discuss Coco Jones recording deal progress, LiAngelo Ball album delivery status, and Phil The Keys producer deal.',
    type: 'meeting',
    meetingLink: 'https://meet.google.com/dj-ar-weekly',
  },
  {
    id: 'dj-ev-2',
    title: 'Coco Jones Deal Review',
    date: getRelativeDate(0),
    startTime: '10:30',
    endTime: '12:00',
    timezone: 'EST',
    participants: ['dj-c1', 'dj-c2', 'demo_user'],
    description: 'Deep dive on Coco Jones recording deal — $2.5M advance, 18% royalty rate, 20% touring, 25% merch. Review all sections before sending to artist management.',
    type: 'contract',
    contractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
    contactId: 'dj-c1',
    meetingLink: 'https://meet.google.com/dj-coco-review',
  },
  {
    id: 'dj-ev-3',
    title: 'Legal Affairs Review',
    date: getRelativeDate(1),
    startTime: '11:00',
    endTime: '12:30',
    timezone: 'EST',
    participants: ['dj-c1', 'dj-c4', 'demo_user'],
    description: 'Weekly legal review with Kecia and UMG counsel. Topics: Bieber amendment status, Big Sean catalog negotiation, sample clearance updates.',
    type: 'review',
    contactId: 'dj-c1',
    meetingLink: 'https://meet.google.com/dj-legal-weekly',
  },
  {
    id: 'dj-ev-4',
    title: 'Sync Committee Meeting',
    date: getRelativeDate(1),
    startTime: '14:00',
    endTime: '15:00',
    timezone: 'EST',
    participants: ['dj-c6', 'dj-c2'],
    description: 'Review sync licensing opportunities — Nike campaign for 2 Chainz, upcoming TV/film placements.',
    type: 'meeting',
    contactId: 'dj-c6',
    meetingLink: 'https://zoom.us/j/dj-sync-committee',
  },
  {
    id: 'dj-ev-5',
    title: 'Bieber Amendment Discussion',
    date: getRelativeDate(2),
    startTime: '09:00',
    endTime: '10:00',
    timezone: 'EST',
    participants: ['dj-c1', 'dj-c3', 'demo_user'],
    description: 'Final discussion on Justin Bieber Album 7 amendment terms. $12M advance structure and royalty modifications.',
    type: 'contract',
    contractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
    contactId: 'dj-c3',
  },
  {
    id: 'dj-ev-6',
    title: 'Nasty C JV Call',
    date: getRelativeDate(3),
    startTime: '15:00',
    endTime: '16:00',
    timezone: 'EST',
    participants: ['dj-c3', 'demo_user'],
    description: 'Discuss Nasty C JV structure with Nasty C team — sublabel scope, territory, and creative direction.',
    type: 'meeting',
    contactId: 'dj-c3',
    meetingLink: 'https://meet.google.com/dj-africa-jv',
  },
  {
    id: 'dj-ev-7',
    title: 'Kith Brand Partnership Strategy',
    date: getRelativeDate(3),
    startTime: '13:00',
    endTime: '14:00',
    timezone: 'EST',
    participants: ['dj-c5'],
    description: 'Initial meeting with Kith on Def Jam branded capsule collection partnership — $5M multi-year deal.',
    type: 'meeting',
    contactId: 'dj-c5',
    meetingLink: 'https://zoom.us/j/dj-livenation',
  },
  {
    id: 'dj-ev-8',
    title: 'Big Sean Catalog Review',
    date: getRelativeDate(4),
    startTime: '10:00',
    endTime: '12:00',
    timezone: 'EST',
    participants: ['dj-c1', 'dj-c3', 'dj-c4', 'demo_user'],
    description: 'Quarterly review of Big Sean catalog buyback negotiation — $8.5M valuation, discuss master reversion terms and payment structure.',
    type: 'review',
    meetingLink: 'https://zoom.us/j/dj-catalog-review',
  },
  {
    id: 'dj-ev-9',
    title: 'Quick Sync — Nicole',
    date: getRelativeDate(0),
    startTime: '16:00',
    endTime: '16:15',
    timezone: 'EST',
    participants: ['dj-c2'],
    description: 'Brief check-in on Phil The Keys producer deal status and LiAngelo Ball delivery timeline.',
    type: 'meeting',
    contactId: 'dj-c2',
  },
  {
    id: 'dj-ev-10',
    title: 'Phil The Keys Signing Deadline',
    date: getRelativeDate(1),
    startTime: '17:00',
    endTime: '17:30',
    timezone: 'EST',
    participants: ['dj-c2'],
    description: 'Final deadline to sign the producer agreement. All terms negotiated and approved.',
    type: 'deadline',
    contractId: DEMO_CONTRACT_IDS.PRODUCER_AGREEMENT,
    contactId: 'dj-c2',
  },
  {
    id: 'dj-ev-12',
    title: 'Nike Sync Terms Review',
    date: getRelativeDate(5),
    startTime: '14:00',
    endTime: '15:00',
    timezone: 'EST',
    participants: ['dj-c6'],
    description: 'Review proposed sync license terms for 2 Chainz catalog usage in Nike Air Max campaign.',
    type: 'meeting',
    contactId: 'dj-c6',
    meetingLink: 'https://meet.google.com/dj-nike-sync',
  },
];

export function getContactById(id: string): CalendarContact | undefined {
  return calendarContacts.find(c => c.id === id);
}

export function getContactName(id: string): string {
  if (id === 'demo_user') return 'You';
  const contact = getContactById(id);
  return contact?.name || id;
}
